import { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Moon, Sun, LogOut, UserCircle, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { SidebarTrigger } from '../SidebarNew/ui/sidebar';
import styles from './DashboardHeader.module.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Skeleton } from 'antd';
import { getNotifications, markNotificationRead, NotificationItem } from '../../api/Notification';
import { getProfile } from '../../api/Profile';
import { isRealtimeEnabled, subscribeNotifications } from '../../services/realtime';

interface DashboardHeaderProps {
  user: {
    email: string;
    fullname?: string;
    avatar_url?: string;
    roleName?: string;
    language?: 'en' | 'vi';
  };
  onLogout?: () => void;
  onProfileClick?: () => void;
}

export function DashboardHeader({
  user,
  onLogout,
  onProfileClick,
}: DashboardHeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Tên kênh/nhóm hiện tại (do trang chat phát qua sự kiện) để hiển thị trên header
  const [chatChannelName, setChatChannelName] = useState('');
  useEffect(() => {
    const onName = (e: any) => setChatChannelName(e?.detail || '');
    window.addEventListener('chat-channel-name', onName);
    return () => window.removeEventListener('chat-channel-name', onName);
  }, []);
  const isChatRoute = location.pathname.startsWith('/chat');
  const headerTitle = (isChatRoute && chatChannelName) ? chatChannelName : 'Viện cấy tóc Newhair by Khơ Thị';
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isOpenLanguageMenu, setIsOpenLanguageMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const lng = i18n.language || 'en';
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Notifications
  const NOTIF_LIMIT = 15;
  const [showNotif, setShowNotif] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bellFlash, setBellFlash] = useState(false);
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tắt/bật âm báo thông báo. Lưu localStorage để giữ lựa chọn qua các phiên.
  // Dùng ref để closure realtime (subscribe chạy 1 lần) luôn đọc trạng thái mới nhất.
  const [soundMuted, setSoundMuted] = useState<boolean>(() => localStorage.getItem('notif_sound_muted') === '1');
  const soundMutedRef = useRef(soundMuted);
  useEffect(() => { soundMutedRef.current = soundMuted; }, [soundMuted]);
  const toggleSoundMuted = () => {
    setSoundMuted((prev) => {
      const next = !prev;
      localStorage.setItem('notif_sound_muted', next ? '1' : '0');
      return next;
    });
  };

  // Beep ngắn bằng Web Audio (không cần file âm thanh)
  const playNotifSound = () => {
    if (soundMutedRef.current) return; // người dùng đã tắt âm báo (chuông vẫn nháy, badge vẫn cập nhật)
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      osc.start();
      osc.stop(ctx.currentTime + 0.36);
      osc.onended = () => ctx.close();
    } catch { /* trình duyệt chặn audio context */ }
  };

  // Rung chuông + nhấp nháy badge khi có thông báo mới
  const notifyNew = () => {
    playNotifSound();
    setBellFlash(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setBellFlash(false), 2200);
  };
  const notifPageRef = useRef(1);
  const notifTotalRef = useRef(0);
  const loadedCountRef = useRef(0);
  const loadingMoreRef = useRef(false);

  // Lấy mảng thông báo dù API trả {items}, paginator {data}, hay mảng trực tiếp
  const extractItems = (data: any): NotificationItem[] => {
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchUnreadCount = useCallback(async () => {
    const res = await getNotifications({ is_read: false, limit: 1 });
    if (res.status && res.data) {
      const total =
        typeof (res.data as any).total === 'number'
          ? (res.data as any).total
          : extractItems(res.data).length;
      setUnreadCount(total);
    }
  }, []);

  // Tải trang đầu (reset)
  const fetchNotifications = useCallback(async () => {
    setLoadingNotif(true);
    notifPageRef.current = 1;
    const res = await getNotifications({ page: 1, limit: NOTIF_LIMIT });
    if (res.status && res.data) {
      const items = extractItems(res.data);
      notifTotalRef.current = res.data.total ?? items.length;
      loadedCountRef.current = items.length;
      setNotifications(items);
    } else {
      notifTotalRef.current = 0;
      loadedCountRef.current = 0;
      setNotifications([]);
    }
    setLoadingNotif(false);
  }, []);

  // Tải trang kế tiếp khi cuộn xuống đáy
  const loadMoreNotifications = useCallback(async () => {
    if (loadingMoreRef.current) return;
    if (loadedCountRef.current >= notifTotalRef.current) return; // hết dữ liệu
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = notifPageRef.current + 1;
    const res = await getNotifications({ page: nextPage, limit: NOTIF_LIMIT });
    if (res.status && res.data) {
      const items = extractItems(res.data);
      notifPageRef.current = nextPage;
      notifTotalRef.current = res.data.total ?? notifTotalRef.current;
      setNotifications((prev) => {
        const ids = new Set(prev.map((n) => n.id));
        const merged = [...prev, ...items.filter((n) => !ids.has(n.id))];
        loadedCountRef.current = merged.length;
        return merged;
      });
    }
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }, []);

  const handleNotifScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    // Gần chạm đáy (còn ~60px) -> tải thêm
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      if (!loadingMoreRef.current && loadedCountRef.current < notifTotalRef.current) {
        loadMoreNotifications();
      }
    }
  };

  // Lấy số chưa đọc 1 lần khi mở trang để hiển thị badge ban đầu.
  // Sau đó KHÔNG polling — chỉ cập nhật khi có tín hiệu realtime khớp filter recipient_id.
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Lấy user_id (= recipient_id của notifications) để lọc realtime đúng người nhận
  useEffect(() => {
    getProfile().then((res) => {
      const uid = res.status ? Number((res.data as any)?.user_id) : NaN;
      if (Number.isFinite(uid)) setRecipientId(uid);
    });
  }, []);

  // Realtime: nghe thông báo mới (INSERT bảng notifications) -> refetch số chưa đọc,
  // và refetch danh sách nếu dropdown đang mở. Đồng thời theo dõi trạng thái kết nối.
  const showNotifRef = useRef(false);
  useEffect(() => { showNotifRef.current = showNotif; }, [showNotif]);
  const notifChannelRef = useRef(`public:notifications:${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!isRealtimeEnabled()) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const debouncedRefresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        // Chỉ cập nhật badge + danh sách (không quyết định âm báo ở đây).
        const res = await getNotifications({ is_read: false, limit: 1 });
        if (res.status && res.data) {
          const total =
            typeof (res.data as any).total === 'number'
              ? (res.data as any).total
              : extractItems(res.data).length;
          setUnreadCount(total);
        }
        if (showNotifRef.current) fetchNotifications();
        // Báo cho trang /notifications (nếu đang mở) nạp lại danh sách realtime
        window.dispatchEvent(new Event('notification-arrived'));
      }, 600);
    };
    const unsub = subscribeNotifications({
      channelName: notifChannelRef.current,
      // Lọc đúng người nhận khi đã biết user_id; chưa biết thì nghe tạm toàn bảng
      // (vẫn đúng nhờ refetch số chưa đọc server-scoped).
      filter: recipientId != null ? `recipient_id=eq.${recipientId}` : undefined,
      onInsert: () => {
        // Tín hiệu đã khớp filter recipient_id -> chắc chắn là thông báo của mình
        // -> kêu + nháy NGAY (không phụ thuộc số lượng tăng/giảm).
        // Khi chưa có recipientId (đang nghe tạm toàn bảng) thì không kêu để tránh báo nhầm.
        if (recipientId != null) notifyNew();
        debouncedRefresh();
      },
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [fetchUnreadCount, fetchNotifications, recipientId]);

  // Trang /notifications bấm "Đánh dấu tất cả đã đọc" -> đưa badge về 0 ngay
  useEffect(() => {
    const onReadAll = () => {
      setUnreadCount(0);
      if (showNotifRef.current) fetchNotifications();
    };
    // Đánh dấu đã đọc TỪNG thông báo từ trang danh sách -> refetch số chưa đọc (chính xác theo server)
    const onReadOne = () => {
      fetchUnreadCount();
      if (showNotifRef.current) fetchNotifications();
    };
    window.addEventListener('notifications-read-all', onReadAll);
    window.addEventListener('notification-read', onReadOne);
    return () => {
      window.removeEventListener('notifications-read-all', onReadAll);
      window.removeEventListener('notification-read', onReadOne);
    };
  }, [fetchNotifications, fetchUnreadCount]);

  // Đóng dropdown thông báo khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotif(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleNotif = () => {
    const next = !showNotif;
    setShowNotif(next);
    if (next) fetchNotifications();
  };

  // Đánh dấu đã đọc (optimistic, tự revert nếu lỗi)
  const markRead = async (n: NotificationItem) => {
    if (n.is_read) return;
    setNotifications((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
    setUnreadCount((c) => Math.max(0, c - 1));
    const res = await markNotificationRead(n.id);
    if (!res.status) {
      setNotifications((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: false } : it)));
      setUnreadCount((c) => c + 1);
    }
  };

  // Bấm vào 1 thông báo: điều hướng theo loại thông báo, đồng thời đánh dấu đã đọc.
  const handleNotifClick = (n: NotificationItem) => {
    if (n.type === 'new_message' && n.channel_connection_id && n.channel_customer_id) {
      setShowNotif(false);
      navigate(`/chat/${n.channel_connection_id}?customer_id=${n.channel_customer_id}`);
    } else if (n.type === 'noti_telesale' && n.related_id) {
      // Giao khách cho telesale -> mở chi tiết lịch hẹn liên quan
      setShowNotif(false);
      navigate(`/appointment/detail/${n.related_id}`);
    } else if (n.type === 'noti_branch_staff' && n.related_id) {
      // Giao khách cho nhân viên cơ sở -> mở chi tiết lịch tư vấn liên quan
      setShowNotif(false);
      navigate(`/consultation/detail/${n.related_id}`);
    } else if (n.type === 'noti_action_staff' && n.related_id) {
      // Giao khách cho bác sĩ/NV thực hiện -> mở chi tiết lịch thực hiện liên quan
      setShowNotif(false);
      navigate(`/execution-schedule/detail/${n.related_id}`);
    } else if (n.type === 'call_failed' && n.related_id) {
      // Báo cuộc gọi thất bại -> mở chi tiết khách hàng liên quan
      setShowNotif(false);
      navigate(`/customer/${n.related_id}`);
    }
    markRead(n);
  };

  const formatNotifTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(lng === 'vi' ? 'vi-VN' : 'en-US', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpenLanguageMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check initial theme by looking at document class
  useEffect(() => {
    setIsDarkMode(document.documentElement.classList.contains('dark'));
  }, []);

  // Change theme
  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    document.documentElement.classList.toggle('dark', newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const toggleCollapseLang = () => {
    setIsOpenLanguageMenu((prev) => !prev);
  };

  // Change language
  const changeLanguage = async (newLng: string) => {
    await i18n.changeLanguage(newLng);
    localStorage.setItem('i18nextLng', newLng);
    setIsOpenLanguageMenu(false);
  }

  // Get user initials
  const getInitials = () => {
    if (user.fullname) {
      return user.fullname
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <header className={styles.header}>
      {/* Left: Sidebar Toggle */}
      <div className={styles.headerLeft}>
        <SidebarTrigger className='icon-sidebar' />
        <div className='back-button-top back-button-text flex items-center gap-1 cursor-pointer text-000000e0 hover:!text-[#3e91ab]' onClick={() => window.history.back()}>
          <ArrowLeft className={styles.icon} />
          {t('header.go_back')}
          <span className="text-gray-300 px-1 sm:block hidden">|</span>
          <span className="font-bold text-gray-800 text-xs uppercase tracking-wider text-000000e0 sm:block hidden">{headerTitle}</span>
        </div>
      </div>

      {/* Right: Utilities */}
      <div className={styles.headerRight}>
        {/* Theme */}
        <button
          className={styles.iconButton}
          onClick={toggleTheme}
          title={isDarkMode ? t('header.lightMode') : t('header.darkMode')}
        >
          {isDarkMode ? (
            <Sun className={styles.icon} />
          ) : (
            <Moon className={styles.icon} />
          )}
        </button>

        {/* Language */}
        <div ref={dropdownRef} className="relative">
          <button
            className={styles.iconButton}
            onClick={toggleCollapseLang}
            title={t('header.language')}
          >
            {lng === 'en' ? (
              <img src="/images/flag/en.png" alt="English" className="!w-full !h-5" />
            ) : (
              <img src="/images/flag/vi.png" alt="Vietnamese" className="!w-full !h-5" />
            )}
          </button>
          {isOpenLanguageMenu && (
            <div className={styles.langDropdown}>
              <button
                className={styles.langItem}
                onClick={() => changeLanguage('en')}
              >
                <img src="/images/flag/en.png" alt="English" />
                {t('english')}
              </button>
              <button
                className={styles.langItem}
                onClick={() => changeLanguage('vi')}
              >
                <img src="/images/flag/vi.png" alt="Vietnamese" />
                {t('vietnamese')}
              </button>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div ref={notifRef} className="relative">
          <button
            className={styles.iconButton}
            title={t('header.notifications')}
            onClick={toggleNotif}
          >
            <Bell className={`${styles.icon} ${bellFlash ? styles.bellRinging : ''}`} />
            {unreadCount > 0 && (
              <span className={`${styles.notificationBadge} ${bellFlash ? styles.badgePulsing : ''}`}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {showNotif && (
            <div
              onScroll={handleNotifScroll}
              className="absolute right-0 mt-2 w-[340px] max-h-[460px] overflow-y-auto rounded-lg shadow-lg border border-gray-200 dark:border-[#303030] bg-white dark:bg-[#1f1f1f] z-50"
            >
              <div className="px-4 py-3 border-b border-gray-100 dark:border-[#303030] font-semibold text-gray-800 dark:text-white sticky top-0 bg-white dark:bg-[#1f1f1f] flex items-center justify-between">
                <div>
                  {t('header.notifications')}
                  {unreadCount > 0 && (
                    <span className="ml-2 text-xs font-normal text-[#3e91ab]">({unreadCount})</span>
                  )}
                </div>
                {/* Tắt/bật âm báo khi có thông báo mới */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSoundMuted(); }}
                  title={soundMuted ? t('header.unmute_sound') : t('header.mute_sound')}
                  className={`p-1.5 rounded-md transition hover:bg-gray-100 dark:hover:bg-[#303030] ${soundMuted ? 'text-gray-400' : 'text-[#3e91ab]'}`}
                >
                  {soundMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
              </div>

              {loadingNotif ? (
                <ul className="divide-y divide-gray-100 dark:divide-[#303030]">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li key={`notif-skeleton-${i}`} className="px-4 py-3">
                      <Skeleton
                        active
                        title={{ width: '55%' }}
                        paragraph={{ rows: 2, width: ['100%', '35%'] }}
                      />
                    </li>
                  ))}
                </ul>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('header.no_notifications')}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-[#303030]">
                  {notifications.map((n) => (
                    <li
                      key={n.id}
                      onClick={() => handleNotifClick(n)}
                      title={n.is_read ? undefined : t('header.mark_read')}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#262626] transition ${n.is_read ? '' : 'bg-[#3e91ab]/5'
                        }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.is_read && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-[#3e91ab] flex-shrink-0" />
                        )}
                        <div className={`flex-1 min-w-0 ${n.is_read ? 'pl-4' : ''}`}>
                          <div className="text-sm font-medium text-gray-800 dark:text-white">
                            {n.title}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">
                            {n.content}
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            {formatNotifTime(n.created_at)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {loadingMore && (
                <div className="px-4 py-3">
                  <Skeleton active title={false} paragraph={{ rows: 2, width: ['100%', '40%'] }} />
                </div>
              )}

              {/* Xem tất cả thông báo -> trang /notifications */}
              <div className="sticky bottom-0 border-t border-gray-100 dark:border-[#303030] bg-white dark:bg-[#1f1f1f]">
                <button
                  onClick={() => { setShowNotif(false); navigate('/notifications'); }}
                  className="w-full py-2.5 text-center text-sm font-medium text-[#3e91ab] hover:bg-gray-50 dark:hover:bg-[#262626] transition"
                >
                  {t('header.view_all_notifications')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className={`header-avata-block ${styles.userMenuWrapper}`} ref={userMenuRef}>
          <button
            className={`header-avata-item ${styles.userButton}`}
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.fullname || user.email}
                className={styles.userAvatar}
              />
            ) : (
              <div className={styles.userAvatarPlaceholder}>
                {getInitials()}
              </div>
            )}
            <div className="hidden md:block">
              <div className="text-[13px] font-medium text-left text-000000e0">{user.fullname || user.email.split('@')[0]}</div>
              {(user.roleName || user.email) && (
                <div className="text-[11px] text-gray-500 text-left">{user.roleName || user.email.split('@')[0]}</div>
              )}
            </div>
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div className={styles.userDropdown}>
              {/* User Info */}
              <div className={styles.userInfo}>
                <p className={styles.userName}>
                  {user.fullname || user.email.split('@')[0]}
                </p>
                <p className={styles.userEmail}>{user.email}</p>
              </div>

              <div className={styles.dropdownDivider} />

              {/* Menu Items */}
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  onProfileClick?.();
                  setShowUserMenu(false);
                }}
              >
                <UserCircle className={styles.dropdownIcon} />
                <span>{t('profile')}</span>
              </button>

              <div className={styles.dropdownDivider} />

              <button
                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                onClick={() => {
                  onLogout?.();
                  setShowUserMenu(false);
                }}
              >
                <LogOut className={styles.dropdownIcon} />
                <span>{t('logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
